/**
 * HW Cart Icon Shake Animation
 *
 * Two shake modes:
 *
 * 1. Add-to-cart shake — triggers on Shopify/Horizon's `cart:update` event
 *    whenever items are added.
 *
 * 2. Idle periodic shake — shakes the cart icon every N seconds (configured
 *    in the editor) to prompt the customer to click the icon. Only fires when
 *    the cart contains at least one item.
 *
 * Required data attributes on <cart-icon>:
 *   data-hw-animation-enabled="true"        — enables both modes
 *   data-hw-animation-duration="800"        — shake duration in ms
 *   data-hw-idle-shake-enabled="true"       — enables periodic idle shake
 *   data-hw-idle-shake-interval="15"        — interval in seconds between shakes
 *
 * Respects @media (prefers-reduced-motion) via CSS.
 */

const SHAKE_CLASS = 'hw-cart-shake';
const CART_UPDATE_EVENT = 'cart:update';

// Track item count client-side so the idle shake knows whether to fire
let currentItemCount = 0;

/**
 * Trigger the shake animation on all enabled cart icons.
 */
function triggerShake() {
  const cartIcons = document.querySelectorAll('cart-icon[data-hw-animation-enabled="true"]');

  cartIcons.forEach((icon) => {
    const durationMs = parseInt(icon.dataset.hwAnimationDuration ?? '800', 10);

    icon.style.setProperty('--hw-cart-animation-duration', `${durationMs}ms`);

    // Remove first to allow re-triggering if already animating
    icon.classList.remove(SHAKE_CLASS);

    // Force reflow so the browser registers the removal before re-adding
    void icon.offsetWidth;

    icon.classList.add(SHAKE_CLASS);

    setTimeout(() => {
      icon.classList.remove(SHAKE_CLASS);
    }, durationMs + 100);
  });
}

// --- Add-to-cart shake ---

document.addEventListener(CART_UPDATE_EVENT, (event) => {
  const { data } = event.detail ?? {};

  // Update our tracked count regardless of source
  currentItemCount = data?.itemCount ?? 0;

  // Only shake when an item is added — not on quantity changes or removes
  if (data?.source !== 'product-form-component' || currentItemCount <= 0) return;

  triggerShake();
});

// --- Idle periodic shake ---

/**
 * Start the idle shake interval for a given cart icon element.
 * Returns the interval ID so it can be cleared if needed.
 */
function startIdleShake(icon) {
  const intervalSeconds = parseInt(icon.dataset.hwIdleShakeInterval ?? '15', 10);
  const intervalMs = Math.max(5, intervalSeconds) * 1000;

  return setInterval(() => {
    // Only shake when the cart has items
    if (currentItemCount <= 0) return;

    triggerShake();
  }, intervalMs);
}

// Initialise after DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Fetch the current cart item count so idle shake is aware from the start
  fetch('/cart.js')
    .then((res) => res.json())
    .then((cart) => {
      currentItemCount = cart.item_count ?? 0;
    })
    .catch(() => {
      // Silent fail — idle shake will start working after the first cart:update
    });

  // Start idle shake intervals for all enabled icons
  document.querySelectorAll('cart-icon[data-hw-animation-enabled="true"][data-hw-idle-shake-enabled="true"]').forEach((icon) => {
    startIdleShake(icon);
  });
});
